import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchConcerts,
  fetchConcert,
  updateConcert,
  createConcert,
  addProgrammeItem,
  deleteProgrammeItem,
  reorderProgramme,
  type UpdateConcertInput,
  type CreateConcertInput,
  type AddProgrammeItemInput,
  type FetchConcertsParams,
} from '@/services/api/concerts'
import { dataKeys } from './useDashboardData'

export const concertKeys = {
  all: ['concerts'] as const,
  lists: () => [...concertKeys.all, 'list'] as const,
  list: (params?: FetchConcertsParams) => [...concertKeys.lists(), params] as const,
  details: () => [...concertKeys.all, 'detail'] as const,
  detail: (id: number) => [...concertKeys.details(), id] as const,
}

export function useConcerts(params?: FetchConcertsParams) {
  return useQuery({
    queryKey: concertKeys.list(params),
    queryFn: () => fetchConcerts(params),
  })
}

export function useConcert(id: number) {
  return useQuery({
    queryKey: concertKeys.detail(id),
    queryFn: () => fetchConcert(id),
    enabled: id > 0,
  })
}

export function useUpdateConcert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateConcertInput }) =>
      updateConcert(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: concertKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: concertKeys.lists() })
      queryClient.invalidateQueries({ queryKey: dataKeys.dashboard() })
    },
  })
}

export function useCreateConcert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateConcertInput) => createConcert(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: concertKeys.lists() })
      queryClient.invalidateQueries({ queryKey: dataKeys.dashboard() })
    },
  })
}

export function useAddProgrammeItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ concertId, input }: { concertId: number; input: AddProgrammeItemInput }) =>
      addProgrammeItem(concertId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: concertKeys.detail(variables.concertId),
      })
      queryClient.invalidateQueries({ queryKey: concertKeys.lists() })
      queryClient.invalidateQueries({ queryKey: dataKeys.dashboard() })
    },
  })
}

export function useDeleteProgrammeItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ concertId, itemId }: { concertId: number; itemId: number }) =>
      deleteProgrammeItem(concertId, itemId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: concertKeys.detail(variables.concertId),
      })
      queryClient.invalidateQueries({ queryKey: concertKeys.lists() })
      queryClient.invalidateQueries({ queryKey: dataKeys.dashboard() })
    },
  })
}

export function useReorderProgramme() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ concertId, itemIds }: { concertId: number; itemIds: number[] }) =>
      reorderProgramme(concertId, itemIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: concertKeys.detail(variables.concertId),
      })
    },
  })
}
